// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useDrawer } from '@shared/ui-kit/hooks/use-drawer';
import { formatRelativeTime } from '@shared/ui-kit/utils';
import { isDefined } from '@shared/utils';
import { maskIban } from '@shared/advisory/aggregation/utils/bank-utils';
import { getProductTypeLabel } from '@shared/advisory/aggregation/utils/get-product-type-label';
import type { SvgIconName } from '@shared/ui-kit/components/app-icon';
import {
  AdvisoryBankProductType,
  type AdvisoryBank,
  type AdvisoryBankProduct,
} from '@shared/data-access/types/brokerage-types';
import { useTranslation } from 'react-i18next';
import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { useShareAccount } from '../share-institutions-button/use-share-account';
import type { TagEntity } from '@shared/data-access/interfaces/redux/tags-management-state-types';

export type ProductSummaryData = {
  icon: SvgIconName;
  typeLabel: string;
  cash: number;
  debt: number;
  investment: number;
  available: number;
  owners: string;
};

export interface UseBankAccountDrawerProps {
  institution?: AdvisoryBank | null;
  product?: AdvisoryBankProduct | null;
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  onShare?: () => void;
  tags?: readonly TagEntity[];
  initialAssignedTags?: readonly TagEntity[];
  onChangeTagIds?: (ids: number[]) => void;
  onCreateTag?: (name: string) => void;
}

/**
 * Hook for the bank account details drawer. Controlled via props; tag logic is
 * provided by the parent (e.g. useDrawerTagManagement in use-bank-accounts).
 */
export const useBankAccountDrawer = (props: UseBankAccountDrawerProps) => {
  const {
    open,
    onClose,
    onOpen,
    onShare: onShareProp,
    product: productProp,
    institution: institutionProp,
    tags,
    initialAssignedTags,
    onChangeTagIds,
    onCreateTag,
  } = props;

  const tagManagement =
    isDefined(tags) &&
    isDefined(initialAssignedTags) &&
    isDefined(onChangeTagIds) &&
    isDefined(onCreateTag)
      ? { tags, initialAssignedTags, onChangeTagIds, onCreateTag }
      : null;

  const currency = useSelector(selectAccountCurrency);
  const { i18n, t } = useTranslation();

  const {
    handleShare: shareAction,
    handleShareClick: internalClick,
    ...shareProps
  } = useShareAccount();

  const product = productProp ?? null;
  const institution = institutionProp ?? null;

  const [DrawerRenderer, drawerControls] = useDrawer({
    open,
    onClose,
    onOpen,
  });

  const onShare = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onShareProp?.();
      internalClick(event);
    },
    [internalClick, onShareProp],
  );

  const handleShare = useCallback(
    async (targetEmail?: string) => {
      if (!product?.iban) return false;
      return await shareAction(
        product.iban,
        product.name ?? undefined,
        targetEmail,
      );
    },
    [product, shareAction],
  );

  const summary = useMemo((): ProductSummaryData | null => {
    if (!product) return null;

    const owners = product.owners?.map(owner => owner.name).join(', ') ?? '-';

    const typeLabel = getProductTypeLabel(product.product_type, product.type, t);

    switch (product.product_type) {
      case AdvisoryBankProductType.Account:
        return {
          icon: 'Wallet',
          typeLabel,
          owners,
          cash: product.cash ?? 0,
          debt: product.debt ?? 0,
          investment: product.investment ?? 0,
          available: product.available ?? 0,
        };
      case AdvisoryBankProductType.Card:
        return {
          icon: 'CreditCard',
          typeLabel,
          owners,
          cash: 0,
          debt: product.disposed ?? 0,
          investment: 0,
          available: 0,
        };
      case AdvisoryBankProductType.Loan:
        return {
          icon: 'HandCoins',
          typeLabel,
          owners,
          cash: 0,
          debt: product.pending_amount ?? 0,
          investment: 0,
          available: 0,
        };
      case AdvisoryBankProductType.Portfolio:
        return {
          icon: 'TrendingUp',
          typeLabel,
          owners,
          cash: 0,
          debt: 0,
          investment: product.investment ?? 0,
          available: 0,
        };
      default:
        return null;
    }
  }, [product, t]);

  const maskedIban = useMemo(
    () => maskIban(product?.iban, 'first-and-last'),
    [product?.iban],
  );

  const lastSyncFormatted = useMemo(
    () =>
      formatRelativeTime(institution?.last_sync, {
        locale: i18n.language,
        style: 'long',
        direction: 'past',
      }),
    [institution?.last_sync, i18n.language],
  );

  return {
    DrawerRenderer,
    open: drawerControls.open,
    onClose: drawerControls.onClose,
    onOpen: drawerControls.onOpen,
    product,
    institution,
    maskedIban,
    lastSyncFormatted,
    summary,
    currency,
    t,
    handleShare,
    onShare,
    tagManagement: tagManagement ?? null,
    ...shareProps,
  } as const;
};
